import React from 'react';
import { Card, CardContent, Typography, Box, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { 
  CreditCard, 
  Language, 
  CheckCircle, 
  Info,
  FlightTakeoff,
  Assignment 
} from '@mui/icons-material';

// Icônes par défaut disponibles
export const defaultIcons = {
  creditCard: <CreditCard />,
  language: <Language />,
  checkCircle: <CheckCircle />,
  info: <Info />,
  flight: <FlightTakeoff />,
  assignment: <Assignment />,
};

/*
EXEMPLE D'UTILISATION :

import FeatureCard, { defaultIcons } from './components/common/FeatureCard';

// Avec icône Material-UI
<FeatureCard
  icon={defaultIcons.checkCircle}
  title="Check eligibility"
  description="Answer a few questions to see if you're eligible for an ETA-IL"
  onClick={() => navigate('/services')}
/>

// Avec icône personnalisée (URL ou composant)
<FeatureCard
  icon="/path/to/icon.svg"
  title="Discover ETA-IL"
  description="Get useful information, check eligibility and apply for an ETA-IL"
  href="https://example.com"
/>

// Avec action personnalisée
<FeatureCard
  icon={<CustomIcon />}
  title="Get Help"
  description="Find answers to your questions"
  onClick={() => console.log('Card clicked!')}
/>
*/

const FeatureCard = ({
  icon,
  title,
  description,
  href,
  onClick,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (href) {
      window.open(href, '_blank');
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  const isLink = !!href;

  return (
    <Card
      component={isLink ? "a" : "button"}
      {...(isLink ? { href, target: "_blank", rel: "noopener noreferrer" } : {})}
      tabIndex={0}
      role="link"
      aria-label={t("common.cardAction", { title, description })}
      onKeyDown={handleKeyDown}
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', sm: '280px', md: '280px' },
        height: { xs: 'auto', sm: '280px', md: '280px' },
        minHeight: { xs: '120px', sm: '280px', md: '280px' },
        display: 'flex',
        flexDirection: { xs: 'row', sm: 'column', md: 'column' },
        position: 'relative',
        cursor: 'pointer',
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 1,
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        outline: 'none',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 24px ${theme.palette.primary.main}20`,
          '& .progress-bar': {
            width: '100%',
          },
        },
        '&:focus': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px',
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 24px ${theme.palette.primary.main}20`,
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px',
        },
        padding: 0,
      }}
      onClick={isLink ? undefined : handleClick}
    >
      {/* Barre de progression bleue animée */}
      <Box
        className="progress-bar"
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          height: 4,
          width: 0,
          backgroundColor: '#0056D7',
          transition: 'width 0.6s ease',
          zIndex: 2,
        }}
      />

      <CardContent
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'column', md: 'column' },
          alignItems: { xs: 'flex-start', sm: 'center', md: 'center' },
          textAlign: { xs: 'left', sm: 'center', md: 'center' },
          p: { xs: 3, sm: 3, md: 3 },
          pt: { xs: 3, sm: 1, md: 1 },
          pb: 3,
        }}
      >
        {/* Icône */}
        {icon && (
          <Box
            sx={{
              width: { xs: 60, sm: 100, md: 100 },
              height: { xs: 60, sm: 100, md: 100 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: { xs: 0, sm: 3, md: 3 },
              mr: { xs: 3, sm: 0, md: 0 },
              flexShrink: 0,
              '& svg': {
                fontSize: { xs: '2rem', sm: '3rem', md: '3rem' },
                color: theme.palette.primary.main,
              },
              '& img': {
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              },
            }}
          >
            {typeof icon === 'string' ? (
              <img src={icon} alt=""/>
            ) : (
              icon
            )}
          </Box>
        )}

        {/* Contenu texte */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Titre */}
          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontWeight: theme.typography.fontWeights?.semiBold || 600,
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.rem' },
              lineHeight: 1.3,
              mb: { xs: 1, sm: 2, md: 0 },
              color: theme.palette.text.primary,
              minHeight: { xs: 'auto', sm: '2.6em', md: '2.6em' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'flex-start', sm: 'center', md: 'center' },
              // wordBreak: 'break-word',
              // hyphens: 'auto',
              textAlign: { xs: 'left', sm: 'center', md: 'center' }
            }}
          >
            {title}
          </Typography>

          {/* Description */}
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: '14px', sm: '14x', md: '15px' },
              lineHeight: 1.5,
              color: theme.palette.text.secondary,
              flexGrow: 1,
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center', md: 'center' },
              minHeight: { xs: 'auto', sm: '4em', md: '4em' },
              // wordBreak: 'break-word',
              // hyphens: 'auto',
              textAlign: { xs: 'left', sm: 'center', md: 'center' }
            }}
          >
            {description}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default FeatureCard; 